'use client';
/**
 * Paramétrage › RECUEIL — la bibliothèque des guides de recueil du catalogue
 * (RQ, 13/09/2026 : « avoir davantage la main pour contrôler son contenu, dans
 * le paramétrage global, et ensuite l'accoler à l'examen »).
 *
 * Un guide = ses textes (préparation, recueil, récipient, acheminement, point de
 * vigilance), sa pastille (couleur, icône) et la liste des examens auxquels il
 * s'attache. Le catalogue affiche l'encart « Recommandations de recueil » sur
 * ces examens-là, et nulle part ailleurs.
 *
 * ── Premier passage ─────────────────────────────────────────────────────────
 *
 * Tant que rien n'est enregistré, l'écran part des guides que le catalogue
 * affiche AUJOURD'HUI (`guidesParDefaut` : textes historiques, corrections déjà
 * faites dans le manuel, rattachements calculés par l'ancienne règle, et le
 * spermogramme, qui n'en avait pas). Un bandeau le dit : rien n'est écrit tant
 * que la RQ n'a pas enregistré.
 *
 * Écriture : `field-config.guidesRecueil`, la bibliothèque ENTIÈRE à chaque
 * enregistrement (le moteur remplace la clé reçue). Réservée à la RQ, contrôlée
 * par le moteur (`erreurGuidesRecueil`), tracée au journal `MAN-PARAMETRAGE`.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { card, inputStyle, EmptyState, Skeleton, useConfirmation } from '@/components/ui';
import { moteur } from './Registres';
import {
  guidesParDefaut, erreurGuides, ICONES_GUIDE, BORNES_GUIDES,
  type GuideRecueil, type IconeGuide, type ExamenPourGuide,
} from '@/shared/guidesRecueil';
import { OPTION_COLORS } from '@/shared/pastillesListes';

import { Bouton } from '@/shared/Bouton';
type Examen = ExamenPourGuide & { archivee?: boolean };

const CHAMPS: [keyof GuideRecueil, string, string][] = [
  ['preparation', 'Préparation', 'Ce que le patient fait avant (jeûne, toilette, abstinence…)'],
  ['recueil', 'Recueil', 'Le geste, pas à pas'],
  ['recipient', 'Récipient', 'Flacon, pot, écouvillon…'],
  ['acheminement', 'Acheminement', 'Température et délai jusqu’au laboratoire'],
  ['points', 'Point de vigilance', 'Affiché en encart ambré ; laisser vide s’il n’y en a pas'],
];

const normaliser = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

function Pastille({ g, taille = 28 }: { g: Pick<GuideRecueil, 'couleur' | 'icone'>; taille?: number }) {
  return (
    <span aria-hidden style={{ display: 'inline-grid', placeItems: 'center', width: taille, height: taille, borderRadius: 'var(--radius-sm)', background: g.couleur, color: 'var(--text-on-primary)', flex: 'none' }}>
      <svg width={taille * 0.55} height={taille * 0.55} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        dangerouslySetInnerHTML={{ __html: (ICONES_GUIDE[g.icone] || ICONES_GUIDE.flask).trace }} />
    </span>
  );
}

export function OngletGuidesRecueil({ estRq, parametres, manualContent }: {
  estRq: boolean;
  /** `field-config.guidesRecueil` tel que lu — `null` tant que rien n'est enregistré. */
  parametres: unknown;
  manualContent: Record<string, string> | null;
}) {
  const { demander, confirmation } = useConfirmation();
  const [examens, setExamens] = useState<Examen[] | null>(null);
  const [guides, setGuides] = useState<GuideRecueil[] | null>(null);
  const [enregistre, setEnregistre] = useState<boolean>(Array.isArray(parametres));
  const [modifie, setModifie] = useState(false);
  const [ouvert, setOuvert] = useState<string | null>(null);
  const [cherche, setCherche] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [message, setMessage] = useState('');
  const [erreur, setErreur] = useState('');
  /* LA VERSION LUE DE `guidesRecueil` (16/10/2026), renvoyée sous `versions` à
     l'écriture : la clé est remplacée ENTIÈRE par le moteur, et ce brouillon
     part de ce que l'onglet a lu. Si la RQ enregistre pendant qu'un autre a
     changé les guides, le moteur refuse (409) au lieu d'écraser — même règle
     que `Parametrage.tsx`. L'onglet relit lui-même le paramétrage à son
     ouverture : la valeur ET sa version viennent de la MÊME lecture, pas de la
     lecture plus ancienne du parent. `null` = moteur qui ne sert pas de
     versions : on n'en envoie pas. */
  const versionLue = useRef<string | null>(null);
  const [relecture, setRelecture] = useState(0);

  useEffect(() => {
    let vivant = true;
    void Promise.all([moteur('manuel-ref/catalogue'), moteur('manuel-ref/parametrage')]).then(([r, rp]) => {
      if (!vivant) return;
      if (!r.ok) { setErreur(r.message); setExamens([]); return; }
      const liste = ((r.data.fiches as Examen[]) || []).filter((f) => !f.archivee);
      setExamens(liste);
      /* La bibliothèque telle que CETTE lecture la sert ; le parent ne reste
         qu'un repli si la relecture échoue. */
      const v = rp.ok ? (rp.data.versions as Record<string, unknown> | undefined)?.guidesRecueil : undefined;
      versionLue.current = typeof v === 'string' ? v : null;
      const lus = rp.ok ? ((rp.data.parametrage as Record<string, unknown> | null)?.guidesRecueil ?? null) : parametres;
      setGuides(Array.isArray(lus) ? (lus as GuideRecueil[]) : guidesParDefaut(liste, manualContent));
      if (rp.ok) { setEnregistre(Array.isArray(lus)); setModifie(false); }
    });
    return () => { vivant = false; };
  }, [parametres, manualContent, relecture]);

  const parCode = useMemo(() => new Map((examens || []).map((e) => [e.code, e])), [examens]);

  if (erreur && !guides) return <EmptyState titre="Catalogue indisponible" indice={erreur} />;
  if (!guides || !examens) return <Skeleton rows={4} />;

  const changer = (id: string, patch: Partial<GuideRecueil>) => {
    setGuides(guides.map((g) => (g.id === id ? { ...g, ...patch } : g)));
    setModifie(true); setMessage('');
  };
  const ajouter = () => {
    let n = guides.length + 1;
    while (guides.some((g) => g.id === `guide-${n}`)) n += 1;
    const id = `guide-${n}`;
    setGuides([...guides, { id, titre: 'Nouveau guide', sousTitre: '', couleur: '#0E9FDE', icone: 'flask', preparation: '', recueil: '', recipient: '', acheminement: '', points: '', examens: [], actif: true }]);
    setOuvert(id); setModifie(true); setMessage('');
  };
  const supprimer = async (g: GuideRecueil) => {
    if (!(await demander(`Supprimer le guide « ${g.titre} » ?\nIl disparaîtra des ${g.examens.length} examen(s) auxquels il est rattaché. Pour le retirer sans le perdre, décochez plutôt « Affiché ».`, { confirmer: 'Supprimer' }))) return;
    setGuides(guides.filter((x) => x.id !== g.id)); setModifie(true); setMessage('');
  };
  const enregistrer = () => {
    const e = erreurGuides(guides);
    if (e) { setErreur(e); return; }
    setEnvoi(true); setErreur(''); setMessage('');
    const corps: Record<string, unknown> = { guidesRecueil: guides };
    if (versionLue.current) corps.versions = { guidesRecueil: versionLue.current };
    void moteur('manuel-ref/parametrage', corps).then((r) => {
      setEnvoi(false);
      if (r.status === 409) {
        /* Quelqu'un a changé les guides depuis la lecture : rien n'est écrit.
           On relit (valeur et version) pour que la RQ voie sa version avant
           de refaire son geste — le brouillon périmé ne se renvoie pas. */
        setErreur(r.message);
        setRelecture((n) => n + 1);
        return;
      }
      if (!r.ok) { setErreur(r.message); return; }
      const n = (r.data.versions as Record<string, unknown> | undefined)?.guidesRecueil;
      if (typeof n === 'string') versionLue.current = n;
      setEnregistre(true); setModifie(false);
      setMessage('Guides enregistrés — le catalogue les affichera à son prochain chargement.');
    });
  };

  const q = normaliser(cherche.trim());
  const suggestions = (g: GuideRecueil) => (q.length < 2 ? [] : examens
    .filter((e) => !g.examens.includes(e.code))
    .filter((e) => normaliser(`${e.code} ${e.libelle || ''} ${(e.synonymes || []).join(' ')}`).includes(q))
    .slice(0, 8));

  return (
    <div>
      {confirmation}
      <div style={{ ...card, padding: 12, marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 6 }}>Guides de recueil</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Chaque guide s’affiche en encart « Recommandations de recueil » sur les examens <strong>que vous lui rattachez</strong>, et
          nulle part ailleurs. Les textes réglés ici remplacent ceux du manuel.
        </div>
        {!enregistre && (
          <div role="status" style={{ marginTop: 8, padding: '8px 11px', borderRadius: 'var(--radius-sm)', background: 'var(--amber-50)', border: '1px solid #EBD9B4', fontSize: 12.5, color: 'var(--amber-700)' }}>
            Rien n’est encore enregistré : ces guides sont ceux que le catalogue affiche aujourd’hui, avec leurs rattachements
            calculés (et le spermogramme, qui n’en avait pas). Relisez-les, puis enregistrez pour en prendre la main.
          </div>
        )}
      </div>

      {message && <div style={{ color: 'var(--green-600)', fontSize: 12.5, marginBottom: 6 }}>{message}</div>}
      {erreur && <div style={{ color: 'var(--red-600)', fontSize: 12.5, marginBottom: 6 }}>{erreur}</div>}

      {guides.map((g) => {
        const est = ouvert === g.id;
        return (
          <div key={g.id} style={{ ...card, padding: 12, marginBottom: 8, opacity: g.actif ? 1 : 0.7 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <Pastille g={g} />
              <button type="button" className="ged-btn" onClick={() => { setOuvert(est ? null : g.id); setCherche(''); }} aria-expanded={est}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', textAlign: 'start', flex: 1, minWidth: 200 }}>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: 'var(--text-strong)' }}>{g.titre}</span>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)' }}>
                  {g.examens.length === 0 ? 'Rattaché à aucun examen' : `${g.examens.length} examen${g.examens.length > 1 ? 's' : ''} : ${g.examens.slice(0, 4).join(', ')}${g.examens.length > 4 ? '…' : ''}`}
                  {!g.actif && ' · masqué'}
                </span>
              </button>
              <Bouton variante={est ? 'primaire' : 'contour'} taille="sm" type="button" onClick={() => { setOuvert(est ? null : g.id); setCherche(''); }}>
                {est ? 'Fermer' : 'Modifier'}
              </Bouton>
            </div>

            {est && (
              <div style={{ marginTop: 12, display: 'grid', gap: 10, maxWidth: 760 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <label style={{ fontSize: 12, fontWeight: 600 }}>Titre
                    <input disabled={!estRq} value={g.titre} maxLength={BORNES_GUIDES.titre} onChange={(e) => changer(g.id, { titre: e.target.value })} style={{ ...inputStyle, marginTop: 3 }} />
                  </label>
                  <label style={{ fontSize: 12, fontWeight: 600 }}>Sous-titre
                    <input disabled={!estRq} value={g.sousTitre} onChange={(e) => changer(g.id, { sousTitre: e.target.value })} style={{ ...inputStyle, marginTop: 3 }} />
                  </label>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', fontSize: 12, fontWeight: 600 }}>
                  <span>Pastille</span>
                  <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                    {OPTION_COLORS.slice(0, 12).map((c) => (
                      <button key={c} type="button" disabled={!estRq} onClick={() => changer(g.id, { couleur: c })} aria-label={`Couleur ${c}`} aria-pressed={g.couleur.toLowerCase() === c.toLowerCase()}
                        style={{ width: 22, height: 22, borderRadius: 'var(--radius-sm)', background: c, border: g.couleur.toLowerCase() === c.toLowerCase() ? '2px solid var(--text-strong)' : '1px solid var(--border-default)', cursor: estRq ? 'pointer' : 'default' }} />
                    ))}
                  </span>
                  <span style={{ display: 'inline-flex', gap: 4 }}>
                    {(Object.keys(ICONES_GUIDE) as IconeGuide[]).map((ic) => (
                      <button key={ic} type="button" disabled={!estRq} onClick={() => changer(g.id, { icone: ic })} title={ICONES_GUIDE[ic].libelle} aria-label={ICONES_GUIDE[ic].libelle} aria-pressed={g.icone === ic}
                        style={{ padding: 2, borderRadius: 'var(--radius-md)', border: g.icone === ic ? '2px solid var(--text-strong)' : '1px solid transparent', background: 'none', cursor: estRq ? 'pointer' : 'default' }}>
                        <Pastille g={{ couleur: g.couleur, icone: ic }} taille={24} />
                      </button>
                    ))}
                  </span>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <input type="checkbox" disabled={!estRq} checked={g.actif} onChange={(e) => changer(g.id, { actif: e.target.checked })} /> Affiché
                  </label>
                </div>

                {CHAMPS.map(([champ, libelle, aide]) => (
                  <label key={champ} style={{ fontSize: 12, fontWeight: 600 }}>{libelle}
                    <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> — {aide}</span>
                    <textarea disabled={!estRq} rows={2} value={String(g[champ])} maxLength={BORNES_GUIDES.texte}
                      onChange={(e) => changer(g.id, { [champ]: e.target.value } as Partial<GuideRecueil>)}
                      style={{ ...inputStyle, height: 'auto', minHeight: 54, padding: '7px 10px', marginTop: 3, resize: 'vertical', lineHeight: 1.45 }} />
                  </label>
                ))}

                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 5 }}>Examens rattachés ({g.examens.length})</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {g.examens.map((code) => {
                      const e = parCode.get(code);
                      return (
                        <span key={code} title={e?.libelle || 'Code absent du catalogue actif'}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 4px 3px 10px', borderRadius: 'var(--radius-pill)', fontSize: 12, border: `1px solid ${e ? 'var(--border-default)' : 'var(--amber-100)'}`, background: e ? 'var(--surface-card)' : 'var(--amber-50)' }}>
                          <strong style={{ fontFamily: 'var(--font-mono)' }}>{code}</strong>{e?.libelle ? ` ${e.libelle}` : ' (absent du catalogue)'}
                          {estRq && (
                            <button type="button" onClick={() => changer(g.id, { examens: g.examens.filter((c) => c !== code) })} aria-label={`Détacher ${code}`}
                              style={{ width: 20, height: 20, borderRadius: '50%', border: 'none', background: 'var(--gray-100)', cursor: 'pointer', lineHeight: 1 }}>×</button>
                          )}
                        </span>
                      );
                    })}
                  </div>
                  {estRq && (
                    <div style={{ position: 'relative', marginTop: 7, maxWidth: 420 }}>
                      <input value={cherche} onChange={(e) => setCherche(e.target.value)} placeholder="Rattacher un examen : code ou libellé…" aria-label="Rechercher un examen à rattacher" style={inputStyle} />
                      {suggestions(g).length > 0 && (
                        <div style={{ position: 'absolute', zIndex: 5, top: '100%', left: 0, right: 0, background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow-md)', marginTop: 3 }}>
                          {suggestions(g).map((e) => (
                            <button key={e.code} type="button" onClick={() => { changer(g.id, { examens: [...g.examens, e.code].sort() }); setCherche(''); }}
                              style={{ display: 'block', width: '100%', textAlign: 'start', padding: '7px 10px', border: 'none', borderBottom: '1px solid var(--border-subtle)', background: 'none', cursor: 'pointer', fontSize: 12.5 }}>
                              <strong style={{ fontFamily: 'var(--font-mono)' }}>{e.code}</strong> {e.libelle}
                              <span style={{ color: 'var(--text-muted)' }}> · {e.famille}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {estRq && (
                  <div>
                    <Bouton variante="danger" taille="sm" type="button" onClick={() => { void supprimer(g); }}>Supprimer ce guide</Bouton>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {estRq ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
          <Bouton taille="sm" type="button" onClick={ajouter}>+ Ajouter un guide</Bouton>
          <Bouton variante="primaire" taille="sm" type="button" disabled={envoi || (!modifie && enregistre)} onClick={enregistrer}>
            {envoi ? 'Enregistrement…' : enregistre ? 'Enregistrer les modifications' : 'Enregistrer et prendre la main'}
          </Bouton>
          {modifie && <span style={{ fontSize: 12, color: 'var(--amber-700)' }}>Modifications non enregistrées</span>}
        </div>
      ) : (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 8 }}>Lecture seule — les guides se règlent par la Responsable Qualité.</div>
      )}
    </div>
  );
}
